// src/components/admin/student/dashboard/tabs/StudentSatPlanTab.js
//
// Student profile "Plan" tab — clean, parent-friendly plan management.
//
//   List view:  horizontal row cards (status, planned hours, current→target,
//               source, progress). Whole row opens the detail; actions live in a
//               single ⋯ menu.
//   Detail view: visual summary (donut hour allocation + summary tiles + overall
//               progress), required R&W + Math side by side with per-item
//               progress bars, then a separate "Not Covered in This Plan".
//
// Editing/regenerating opens the full-page workspace in a new tab; we refresh on
// focus so changes appear when the admin returns.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FiMoreHorizontal, FiCheckCircle, FiChevronDown, FiChevronUp } from "react-icons/fi";
import PlanProgressHistory from "../../../../sat/PlanProgressHistory";
import ProgressGauge from "../../../../sat/ProgressGauge";
import PlanSummaryCard from "../../../../sat/PlanSummaryCard";
import { planSubjectOrder, planTypeOf, PLAN_TYPES, isCustomPlan } from "../../../../../utils/plan/planConfig";
import { openCustomPlanTab, openPlanEditor, openPlanTab } from "../../../../../utils/plan/planLinks";
import { TUTOR_PERMISSIONS } from "../permissions";
import { auth } from "../../../../../firebaseConfig";
import {
  fetchPlansForStudentDir,
  fetchActivePlansForDiagnosticUid,
  summarizePlanProgress,
  derivePlanDisplayStatus,
  itemProgressPct,
  duplicatePlan,
  updatePlanStatus,
  setActivePlan,
  deletePlan,
  requestPlanDeletion,
  restorePlan,
  purgeExpiredPlanDeletions,
  isPendingDeletion,
  deletionDaysLeft,
  DELETION_WINDOW_DAYS,
  tsToMs,
} from "../../../../../utils/sat/satPlanData";

function fmtMs(ms) {
  if (!ms) return "—";
  const d = new Date(ms);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
}
const fmtTs = (ts) => fmtMs(tsToMs(ts));

const STATUS_BADGE = {
  active: { label: "Active", bg: "#dcfce7", color: "#166534" },
  completed: { label: "Completed", bg: "#dbeafe", color: "#1e40af" },
  draft: { label: "Draft", bg: "#f1f5f9", color: "#475569" },
  archived: { label: "Archived", bg: "#f1f5f9", color: "#94a3b8" },
};
const ITEM_STATUS = {
  done: { label: "Done", bg: "#dcfce7", color: "#166534", bar: "#16a34a" },
  in_progress: { label: "In progress", bg: "#fef9c3", color: "#92400e", bar: "#f59e0b" },
  todo: { label: "To do", bg: "#f1f5f9", color: "#475569", bar: "#cbd5e1" },
};

function statusOf(it) {
  const p = itemProgressPct(it);
  return p >= 100 ? "done" : p > 0 ? "in_progress" : "todo";
}

export default function StudentSatPlanTab({ student, readOnly = false, permissions }) {
  // Permissions arrive through the shared StudentDashboard tab props. Default to
  // the least-privileged (tutor) set so a caller that forgets to pass them can
  // never accidentally expose admin-only actions.
  const perms = permissions || TUTOR_PERMISSIONS;
  const canManagePlans = !readOnly && !!perms.managePlans;
  const canRequestDeletion = !readOnly && !!perms.requestPlanDeletion;
  const canManageDeleted = !readOnly && !!perms.manageDeletedPlans;

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [view, setView] = useState({ mode: "list", planId: "" });
  const [searchParams] = useSearchParams();
  const appliedDeepLink = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      let list = await fetchPlansForStudentDir(student?.id, { includePending: canManageDeleted });
      if ((!list || !list.length) && student?.diagnosticUid) {
        // Unlinked-profile fallback: a student may hold several active plans.
        const fallback = await fetchActivePlansForDiagnosticUid(student.diagnosticUid);
        if (fallback?.length) list = fallback;
      }
      // Lazy purge: permanently remove plans whose 30-day restore window has
      // elapsed. Firestore deletes are admin-only, so this only runs for admins.
      if (canManageDeleted && list?.length) {
        const purged = await purgeExpiredPlanDeletions(list);
        if (purged) list = list.filter((p) => !isPendingDeletion(p) || deletionDaysLeft(p) > 0);
      }
      setPlans(list || []);
    } catch (e) {
      setError(e?.message || "Failed to load plans.");
    } finally {
      setLoading(false);
    }
  }, [student?.id, student?.diagnosticUid, canManageDeleted]);

  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);

  // Deep link: ?plan=<id> opens that specific plan's detail once it's loaded.
  useEffect(() => {
    if (appliedDeepLink.current) return;
    const pid = searchParams.get("plan");
    if (pid && plans.some((p) => p.id === pid)) {
      setView({ mode: "detail", planId: pid });
      appliedDeepLink.current = true;
    }
  }, [plans, searchParams]);

  const selected = useMemo(() => plans.find((p) => p.id === view.planId) || null, [plans, view.planId]);

  const onEdit = (plan) =>
    openPlanEditor(plan, { studentId: student?.id || "", studentName: student?.name || "" });
  const onNewCustomPlan = () =>
    openCustomPlanTab(
      `studentId=${encodeURIComponent(student?.id || "")}&name=${encodeURIComponent(student?.name || "")}`
    );
  const onRegenerate = (plan) => openPlanTab(`mode=generate&planId=${encodeURIComponent(plan.id)}`);
  const onDuplicate = async (plan) => {
    await duplicatePlan(plan);
    await load();
  };
  const onArchive = async (plan) => {
    if (!window.confirm("Archive this plan? It will no longer be the active plan.")) return;
    await updatePlanStatus(plan.id, "archived");
    await load();
  };
  const onSetActive = async (plan) => {
    await setActivePlan(plan.studentDirId || student?.id, plan.id);
    await load();
  };
  const onDelete = async (plan) => {
    if (!window.confirm("Permanently delete this plan? This cannot be undone.")) return;
    await deletePlan(plan.id);
    if (view.planId === plan.id) setView({ mode: "list", planId: "" });
    await load();
  };
  const onRequestDeletion = async (plan) => {
    const label = plan.title || "this plan";
    const ok = window.confirm(
      `Delete ${label}?\n\n` +
        "It will be hidden from tutors right away and marked for deletion. " +
        (canManageDeleted
          ? `You can restore it or delete it permanently within ${DELETION_WINDOW_DAYS} days.`
          : `An admin can restore it within ${DELETION_WINDOW_DAYS} days, after which it is permanently deleted.`)
    );
    if (!ok) return;
    const user = auth.currentUser;
    await requestPlanDeletion(plan.id, { uid: user?.uid || null, name: user?.displayName || user?.email || null });
    await load();
  };
  const onRestore = async (plan) => {
    await restorePlan(plan.id);
    await load();
  };

  if (loading) return <div style={st.muted}>Loading plans…</div>;
  if (error) return <div style={st.error}>{error}</div>;

  const toolbar = canManagePlans ? (
    <div style={st.toolbar}>
      <div style={st.toolbarTitle}>Plans</div>
      <button style={st.newBtn} onClick={onNewCustomPlan}>
        + New custom plan
      </button>
    </div>
  ) : null;
  if (view.mode === "detail" && selected) {
    return (
      <PlanDetail
        plan={selected}
        canEdit={
          isPendingDeletion(selected)
            ? false
            : isCustomPlan(selected)
              ? canManagePlans
              : canManageDeleted
        }
        onBack={() => setView({ mode: "list", planId: "" })}
        onEdit={() => onEdit(selected)}
      />
    );
  }

  if (!plans.length) {
    return (
      <>
        {toolbar}
        <div style={st.empty}>
          <div style={st.emptyTitle}>No plan yet</div>
          <p style={st.emptyText}>
            {readOnly
              ? "This student doesn't have a plan yet."
              : "Create a custom plan for any subject, or generate one from a completed SAT or ACT diagnostic report (open the report and click “Generate Plan”). Plans appear here once saved."}
          </p>
        </div>
      </>
    );
  }

  // Permission flags handed down to the row action menus.
  const actionFlags = { canManagePlans, canRequestDeletion, canManageDeleted };

  return (
    <>
      {toolbar}
      <div style={st.list}>
        {plans.map((p) => (
          <RowCard
            key={p.id}
            plan={p}
            flags={actionFlags}
            onOpen={() => setView({ mode: "detail", planId: p.id })}
            actions={{
              onEdit,
              onRegenerate,
              onDuplicate,
              onArchive,
              onSetActive,
              onDelete,
              onRequestDeletion,
              onRestore,
            }}
          />
        ))}
      </div>
    </>
  );
}

function RowCard({ plan, onOpen, actions, readOnly, flags }) {
  const display = derivePlanDisplayStatus(plan);
  const badge = STATUS_BADGE[display] || STATUS_BADGE.draft;
  const prog = summarizePlanProgress(plan);
  const isActive = plan.status === "active";
  const gap = plan.scoreGap != null ? plan.scoreGap : null;
  const type = planTypeOf(plan);
  const custom = isCustomPlan(plan);
  const pending = isPendingDeletion(plan);

  return (
    <div style={{ ...st.row, ...(pending ? st.rowPending : null) }} onClick={onOpen} role="button">
      <div style={st.rowLeft}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={st.typeChip}>{PLAN_TYPES[type].label}</span>
          <span style={{ ...st.badge, background: badge.bg, color: badge.color }}>{badge.label}</span>
          {pending && (
            <span style={{ ...st.badge, background: "#fef2f2", color: "#b91c1c" }}>
              Deletes in {deletionDaysLeft(plan)}d
            </span>
          )}
        </div>
        <div style={st.rowSource}>
          {custom ? plan.title || "Custom plan" : plan.attemptLabel || `${type} diagnostic`}
        </div>
        <div style={st.rowDate}>
          {custom ? `Created ${fmtTs(plan.createdAt)}` : fmtTs(plan.confirmedAt || plan.createdAt)}
        </div>
      </div>

      <div style={st.rowStats}>
        <Stat value={`${plan.totalHours || 0}h`} label="Planned" />
        {custom ? (
          <>
            <Stat value={`${prog.doneCount}/${prog.requiredCount}`} label="Items" />
            <Stat value={`${prog.percent}%`} label="Progress" accent />
          </>
        ) : (
          <>
            <Stat value={plan.currentScore ?? plan.totalScore ?? "—"} label="Current" />
            <Stat value={plan.targetScore ?? "—"} label="Target" />
            <Stat value={gap != null ? `+${gap}` : "—"} label="Gap" accent />
          </>
        )}
      </div>

      <div style={st.rowGauge}>
        <ProgressGauge percent={prog.percent} size={48} stroke={6} />
        <div>
          <div style={st.rowGaugeMain}>{prog.doneCount}/{prog.requiredCount} items</div>
          <div style={st.rowGaugeSub}>{prog.coveredHours}/{prog.plannedHours}h</div>
        </div>
      </div>

      {!readOnly && flags && <OverflowMenu plan={plan} isActive={isActive} actions={actions} flags={flags} />}
    </div>
  );
}

function OverflowMenu({ plan, isActive, actions, flags }) {
  const [open, setOpen] = useState(false);
  const stop = (e) => e.stopPropagation();
  const run = (fn) => (e) => {
    e.stopPropagation();
    setOpen(false);
    fn(plan);
  };

  const pending = isPendingDeletion(plan);
  const custom = isCustomPlan(plan);
  // Custom plans are editable by anyone who can manage plans; SAT/ACT plans are
  // edited through the diagnostic workspace, which stays admin-only.
  const canEdit = custom ? flags.canManagePlans : flags.canManageDeleted;

  const items = [];
  if (pending) {
    if (flags.canManageDeleted) {
      items.push(<button key="restore" style={st.menuItem} onClick={run(actions.onRestore)}>Restore</button>);
      items.push(
        <button key="purge" style={{ ...st.menuItem, color: "#b91c1c" }} onClick={run(actions.onDelete)}>
          Delete permanently
        </button>
      );
    }
  } else {
    if (canEdit) items.push(<button key="edit" style={st.menuItem} onClick={run(actions.onEdit)}>Edit</button>);
    if (!custom && flags.canManageDeleted)
      items.push(<button key="regen" style={st.menuItem} onClick={run(actions.onRegenerate)}>Regenerate</button>);
    if (flags.canManageDeleted)
      items.push(<button key="dup" style={st.menuItem} onClick={run(actions.onDuplicate)}>Duplicate</button>);
    if (flags.canManagePlans && !isActive)
      items.push(<button key="active" style={st.menuItem} onClick={run(actions.onSetActive)}>Set active</button>);
    if (flags.canManagePlans && isActive)
      items.push(<button key="archive" style={st.menuItem} onClick={run(actions.onArchive)}>Archive</button>);
    // Deletion is deliberately two-step: soft-delete first (hidden from tutors),
    // then a second visit to this menu on the pending plan offers
    // Restore / Delete permanently. Admins never hard-delete a live plan.
    if (flags.canRequestDeletion) {
      items.push(
        <button key="reqDel" style={{ ...st.menuItem, color: "#b91c1c" }} onClick={run(actions.onRequestDeletion)}>
          {flags.canManageDeleted ? "Delete plan" : "Request deletion"}
        </button>
      );
    }
  }

  if (!items.length) return null;

  return (
    <div style={{ position: "relative" }} onClick={stop}>
      <button style={st.menuBtn} onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }} aria-label="Plan actions">
        <FiMoreHorizontal size={18} />
      </button>
      {open && (
        <>
          <div style={st.menuBackdrop} onClick={(e) => { e.stopPropagation(); setOpen(false); }} />
          <div style={st.menu}>{items}</div>
        </>
      )}
    </div>
  );
}

function Stat({ value, label, accent }) {
  return (
    <div style={st.stat}>
      <div style={{ ...st.statVal, ...(accent ? { color: "#16a34a" } : null) }}>{value}</div>
      <div style={st.statLabel}>{label}</div>
    </div>
  );
}

function PlanDetail({ plan, onBack, onEdit, canEdit }) {
  const order = planSubjectOrder(plan);
  const checklist = plan.checklist || [];
  const required = checklist.filter((i) => !i.isStretch);
  const notCovered = checklist.filter((i) => i.isStretch);

  const reqBySubject = {};
  required.forEach((it) => (reqBySubject[it.subject] = reqBySubject[it.subject] || []).push(it));
  const ncBySubject = {};
  notCovered.forEach((it) => (ncBySubject[it.subject] = ncBySubject[it.subject] || []).push(it));
  const reqSubjects = order.filter((s) => reqBySubject[s]);
  const ncSubjects = order.filter((s) => ncBySubject[s]);

  return (
    <div>
      <div style={st.detailTop}>
        <button style={st.backBtn} onClick={onBack}>← All plans</button>
        {canEdit && <button style={st.editBtn} onClick={onEdit}>Edit plan</button>}
      </div>

      {/* Visual summary */}
      <PlanSummaryCard plan={plan} />

      {/* Summary + reasoning */}
      {(plan.writtenPlan?.parentSummary || plan.writtenPlan?.reasoning) && (
        <div style={st.section}>
          {plan.writtenPlan?.parentSummary && (
            <>
              <div style={st.sectionLabel}>Plan Summary</div>
              <p style={st.prose}>{plan.writtenPlan.parentSummary}</p>
            </>
          )}
          {plan.writtenPlan?.reasoning && (
            <>
              <div style={{ ...st.sectionLabel, marginTop: 12 }}>Why This Plan</div>
              <p style={st.prose}>{plan.writtenPlan.reasoning}</p>
            </>
          )}
        </div>
      )}

      {/* Required, side by side */}
      <div style={st.subjectsGrid}>
        {reqSubjects.map((subject) => {
          const items = reqBySubject[subject];
          const hrs = items.reduce((a, b) => a + (Number(b.allocatedHours) || 0), 0);
          const coveredHrs = items.reduce((a, b) => a + (Number(b.coveredHours) || 0), 0);
          const subjPct =
            hrs > 0
              ? Math.round(items.reduce((a, b) => a + (Number(b.allocatedHours) || 0) * itemProgressPct(b), 0) / hrs)
              : 0;
          return (
            <div style={st.section} key={subject}>
              <div style={st.subjectHead}>
                <span style={st.subjectTitle}>{subject}</span>
                <span style={st.subjectMeta}>{items.length} focus · {coveredHrs}/{hrs}h</span>
              </div>
              <div style={st.subjBar}>
                <div style={{ ...st.subjBarInner, width: `${subjPct}%` }} />
              </div>
              {items.map((it, i) => (
                <DetailRow key={it.id} item={it} rank={i + 1} />
              ))}
            </div>
          );
        })}
      </div>

      {/* Not Covered, full width below */}
      {ncSubjects.length > 0 && (
        <div style={st.notCovered}>
          <div style={st.ncHead}>
            <span style={st.ncTitle}>Not Covered in This Plan</span>
            <span style={st.ncSub}>Still diagnostic weaknesses, but not included because the planned hours are limited.</span>
          </div>
          <div style={st.subjectsGrid}>
            {ncSubjects.map((subject) => (
              <div key={subject}>
                <div style={st.ncSubject}>{subject}</div>
                {ncBySubject[subject].map((it) => (
                  <DetailRow key={it.id} item={it} dim />
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function DetailRow({ item, rank, dim }) {
  const [open, setOpen] = useState(false);
  const pct = itemProgressPct(item);
  const status = statusOf(item);
  const meta = ITEM_STATUS[status];
  const done = status === "done";
  const covered = Number(item.coveredHours) || 0;
  return (
    <div style={{ ...st.itemRow, opacity: dim ? 0.8 : 1 }}>
      <div style={st.itemTop}>
        <div style={st.itemLeft}>
          {rank != null &&
            (done ? (
              <FiCheckCircle size={18} color="#059669" style={{ flexShrink: 0, marginTop: 1 }} />
            ) : (
              <span style={st.rank}>{rank}</span>
            ))}
          <div style={{ minWidth: 0 }}>
            <div style={{ ...st.itemTitle, textDecoration: done ? "line-through" : "none", color: done ? "#94a3b8" : "#0f172a" }}>
              {item.subcategory}
            </div>
            {(item.majorCategory || item.accuracyPct != null) && (
              <div style={st.itemSub}>
                {item.majorCategory}
                {item.accuracyPct != null ? `${item.majorCategory ? " · " : ""}${item.accuracyPct}%` : ""}
              </div>
            )}
          </div>
        </div>
        <div style={st.itemRight}>
          {!dim && <span style={st.hours}>{covered > 0 ? `${covered}/${item.allocatedHours}` : item.allocatedHours}h</span>}
          {!dim && <span style={{ ...st.itemTag, background: meta.bg, color: meta.color }}>{pct}%</span>}
          <button style={st.chev} onClick={() => setOpen((o) => !o)} aria-label="Details">
            {open ? <FiChevronUp size={15} /> : <FiChevronDown size={15} />}
          </button>
        </div>
      </div>
      {!dim && (
        <div style={st.itemBarOuter}>
          <div style={{ ...st.itemBarInner, width: `${pct}%`, background: meta.bar }} />
        </div>
      )}
      {open &&
        (dim ? (
          item.guidance ? <div style={st.dimGuide}>{item.guidance}</div> : <div style={st.dimGuide}>No guidance.</div>
        ) : (
          <div style={st.detailsBox}>
            <PlanProgressHistory item={item} guidance />
          </div>
        ))}
    </div>
  );
}

const st = {
  muted: { color: "#64748b", fontSize: 14, padding: "12px 0" },
  error: { background: "#fef2f2", border: "1px solid #fca5a5", color: "#b91c1c", padding: "10px 12px", borderRadius: 8, fontSize: 13 },
  empty: { padding: "24px 0", maxWidth: 580 },
  emptyTitle: { fontSize: 15, fontWeight: 700, color: "#0f172a", marginBottom: 6 },
  emptyText: { fontSize: 13, color: "#475569", lineHeight: 1.6, margin: 0 },

  list: { display: "flex", flexDirection: "column", gap: 10 },
  toolbar: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 12 },
  toolbarTitle: { fontSize: 13, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em", color: "#6b7280" },
  newBtn: { border: "none", background: "#0f172a", color: "#fff", padding: "8px 14px", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 700 },
  row: { display: "flex", alignItems: "center", gap: 20, border: "1px solid #e5e7eb", borderRadius: 12, padding: "16px 18px", background: "#fff", cursor: "pointer" },
  rowPending: { borderColor: "#fecaca", background: "#fffbfb" },
  rowLeft: { minWidth: 220, flex: "1 1 240px" },
  badge: { fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", padding: "2px 8px", borderRadius: 999 },
  typeChip: { fontSize: 11, fontWeight: 800, letterSpacing: "0.04em", padding: "2px 8px", borderRadius: 6, background: "#0f172a", color: "#fff" },
  rowSource: { fontSize: 13, color: "#334155", marginTop: 8, fontWeight: 600 },
  rowDate: { fontSize: 11.5, color: "#94a3b8", marginTop: 2 },
  rowStats: { display: "flex", gap: 22, flexShrink: 0 },
  stat: {},
  statVal: { fontSize: 18, fontWeight: 800, color: "#0f172a", lineHeight: 1 },
  statLabel: { fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: "#94a3b8", marginTop: 5 },
  rowGauge: { display: "flex", alignItems: "center", gap: 10, flexShrink: 0, width: 150 },
  rowGaugeMain: { fontSize: 13, fontWeight: 700, color: "#0f172a" },
  rowGaugeSub: { fontSize: 12, color: "#64748b", marginTop: 2 },

  menuBtn: { border: "none", background: "transparent", color: "#94a3b8", cursor: "pointer", padding: 6, borderRadius: 8, display: "inline-flex" },
  menuBackdrop: { position: "fixed", inset: 0, zIndex: 20 },
  menu: { position: "absolute", top: "100%", right: 0, marginTop: 4, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, boxShadow: "0 12px 28px rgba(0,0,0,0.14)", zIndex: 21, minWidth: 160, overflow: "hidden" },
  menuItem: { display: "block", width: "100%", textAlign: "left", border: "none", background: "#fff", padding: "10px 14px", fontSize: 13, color: "#334155", cursor: "pointer" },

  detailTop: { display: "flex", justifyContent: "space-between", marginBottom: 14 },
  backBtn: { border: "1px solid #d1d5db", background: "#fff", color: "#334155", padding: "6px 12px", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600 },
  editBtn: { border: "none", background: "#0f172a", color: "#fff", padding: "6px 14px", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 700 },

  head: { border: "1px solid #e5e7eb", borderRadius: 14, padding: 20, background: "#fff", marginBottom: 14 },
  headRow: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 16 },
  headMeta: { fontSize: 12.5, color: "#64748b" },
  headMain: { display: "flex", gap: 28, alignItems: "center", flexWrap: "wrap", marginBottom: 18 },
  donutBox: { display: "flex", alignItems: "center", gap: 18 },
  tiles: { display: "grid", gridTemplateColumns: "repeat(3, minmax(78px, 1fr))", gap: 12, flex: 1, minWidth: 260 },
  tile: { background: "#f8fafc", border: "1px solid #eef2f7", borderRadius: 10, padding: "10px 12px" },
  tileVal: { fontSize: 19, fontWeight: 800, color: "#0f172a", lineHeight: 1 },
  tileLabel: { fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: "#94a3b8", marginTop: 5 },
  progressRow: { display: "flex", alignItems: "center", gap: 18 },
  progBigMain: { fontSize: 15, fontWeight: 800, color: "#0f172a" },
  progBigSub: { fontSize: 13, color: "#64748b", marginTop: 3 },

  section: { border: "1px solid #e5e7eb", borderRadius: 14, padding: 16, background: "#fff", marginBottom: 14 },
  sectionLabel: { fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#6b7280", marginBottom: 8 },
  prose: { fontSize: 13.5, color: "#334155", lineHeight: 1.6, margin: 0 },
  subjectsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: 14 },
  subjectHead: { display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 },
  subjectTitle: { fontSize: 14, fontWeight: 800, color: "#0f172a", textTransform: "uppercase", letterSpacing: "0.04em" },
  subjectMeta: { fontSize: 12, color: "#94a3b8", fontWeight: 600 },
  subjBar: { height: 10, background: "#e2e8f0", borderRadius: 999, overflow: "hidden", marginBottom: 12 },
  subjBarInner: { height: "100%", background: "#059669", borderRadius: 999 },

  notCovered: { border: "1px dashed #d8dee6", borderRadius: 14, padding: 16, background: "#fafbfc", marginBottom: 14 },
  ncHead: { marginBottom: 12 },
  ncTitle: { fontSize: 13.5, fontWeight: 800, color: "#334155", textTransform: "uppercase", letterSpacing: "0.04em" },
  ncSub: { display: "block", fontSize: 12.5, color: "#94a3b8", marginTop: 4 },
  ncSubject: { fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em", color: "#94a3b8", marginBottom: 6 },

  itemRow: { padding: "10px 0", borderTop: "1px solid #f5f7fa" },
  itemTop: { display: "flex", justifyContent: "space-between", gap: 10 },
  itemLeft: { display: "flex", gap: 10, alignItems: "flex-start", minWidth: 0 },
  rank: { fontSize: 11, fontWeight: 800, background: "#0f172a", color: "#fff", borderRadius: 999, minWidth: 20, height: 20, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 },
  itemTitle: { fontSize: 13.5, fontWeight: 700 },
  itemSub: { fontSize: 12, color: "#64748b", marginTop: 1 },
  itemRight: { display: "flex", alignItems: "center", gap: 8, flexShrink: 0 },
  hours: { fontSize: 13, fontWeight: 700, color: "#0f172a" },
  itemTag: { fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 999, whiteSpace: "nowrap" },
  chev: { border: "none", background: "transparent", color: "#94a3b8", cursor: "pointer", padding: 2, display: "inline-flex" },
  itemBarOuter: { height: 9, background: "#e2e8f0", borderRadius: 999, overflow: "hidden", marginTop: 8, marginLeft: 30 },
  itemBarInner: { height: "100%", borderRadius: 999 },
  detailsBox: { marginTop: 12, marginLeft: 30, paddingTop: 12, borderTop: "1px dashed #eef2f7" },
  dimGuide: { marginTop: 10, fontSize: 12.5, color: "#475569", lineHeight: 1.55 },
};
